

import React, { useState, useLayoutEffect, useRef } from 'react';
import { Tab } from '../types';
import { ArrowLeftIcon, ArrowRightIcon, ChartPieIcon, UsersIcon, TableCellsIcon, BarcodeIcon, DocumentChartBarIcon, PaperAirplaneIcon, BellIcon, RocketLaunchIcon, XMarkIcon, KeyIcon } from './Icons';

interface TourStep {
  target?: string;
  title: string;
  content: string;
  action?: () => void;
  icon: React.ReactNode;
}

interface AppTourProps {
  isOpen: boolean;
  onComplete: () => void;
  setActiveTab: (tab: Tab) => void;
}

const tourSteps: (setActiveTab: (tab: Tab) => void) => TourStep[] = (setActiveTab) => [
  {
    icon: <PaperAirplaneIcon className="w-16 h-16 text-blue-400" />,
    title: 'Welcome to the Live Demo!',
    content: "Let's take a quick tour of the app's main features.",
  },
  {
    target: '[data-tour-id="main-tabs"]',
    icon: <ChartPieIcon className="w-12 h-12 text-blue-400" />,
    title: '1. Main Navigation',
    content: 'Navigate between screens using these tabs. Your view is based on your user role.',
  },
  {
    target: '[data-tour-id="flight-tabs"]',
    icon: <PaperAirplaneIcon className="w-12 h-12 text-blue-400" />,
    title: '2. Multi-Flight Management',
    content: 'If enabled by your admin, you can manage multiple flights at once using these tabs. Add a new flight or switch between active ones.',
  },
  {
    target: '[data-tour-id="crew-movement-station"]',
    icon: <UsersIcon className="w-12 h-12 text-blue-400" />,
    title: '3. Track Crew Movement',
    content: 'Tap these icons to log timestamps for Crew and Pilots at each station.',
    action: () => setActiveTab(Tab.CrewMovement),
  },
  {
    target: '[data-tour-id="counter-distribution-inputs"]',
    icon: <TableCellsIcon className="w-12 h-12 text-blue-400" />,
    title: '4. Distribute Staff',
    content: 'Enter flight details, booking figures, and staff assignments here.',
    action: () => setActiveTab(Tab.CounterDistribution),
  },
  {
    target: '[data-tour-id="security-scan-button"]',
    icon: <BarcodeIcon className="w-12 h-12 text-blue-400" />,
    title: '5. Security Scanning',
    content: "Scan passenger boarding passes using your device's camera or enter them manually.",
    action: () => setActiveTab(Tab.Security),
  },
  {
    target: '[data-tour-id="reports-tab"]',
    icon: <DocumentChartBarIcon className="w-12 h-12 text-blue-400" />,
    title: '6. View Reports',
    content: 'Access, view, and export reports for all completed flights.',
    action: () => setActiveTab(Tab.Reports),
  },
  {
    target: '[data-tour-id="news-bar"]',
    icon: <BellIcon className="w-12 h-12 text-red-400" />,
    title: '7. News & Announcements',
    content: 'Key alerts and messages from your team appear in this scrolling bar.',
    action: () => setActiveTab(Tab.FlightDashboard),
  },
  {
    target: '[data-tour-id="profile-button"]',
    icon: <KeyIcon className="w-12 h-12 text-amber-400" />,
    title: '8. Your Profile & Security',
    content: 'Click here to manage your password, set up a quick-login PIN, enable two-factor authentication (2FA), and control push notifications.',
  },
  {
    icon: <RocketLaunchIcon className="w-16 h-16 text-green-400" />,
    title: "You're Ready for Takeoff!",
    content: "You've completed the tour and are all set to manage your flight operations. Click 'Finish' to get started.",
  },
];

const AppTour: React.FC<AppTourProps> = ({ isOpen, onComplete, setActiveTab }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [styles, setStyles] = useState<{
    highlight: React.CSSProperties;
    tooltip: React.CSSProperties;
    arrow: React.CSSProperties;
  }>({
    highlight: { display: 'none' },
    tooltip: { opacity: 0 },
    arrow: {},
  });
  
  const tooltipRef = useRef<HTMLDivElement>(null);
  const steps = tourSteps(setActiveTab);
  const step = steps[currentStep];

  useLayoutEffect(() => {
    if (!isOpen || !step) return;
    
    setIsVisible(false);
    step.action?.();

    const calculatePosition = () => {
      let targetElement: Element | null = null;
      if (step.target) {
        targetElement = document.querySelector(step.target);
        // Ensure the target element is visible before calculating position
        targetElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // Delay position calculation to allow for scrolling to finish
      const positionDelay = targetElement ? 400 : 0;
      
      setTimeout(() => {
        const tooltipElement = tooltipRef.current;
        if (!tooltipElement) return;

        if (targetElement) {
          const targetRect = targetElement.getBoundingClientRect();
          const tooltipRect = tooltipElement.getBoundingClientRect();
          const { innerWidth, innerHeight } = window;
          const margin = 15;

          const highlightStyle: React.CSSProperties = {
            width: targetRect.width + 12,
            height: targetRect.height + 12,
            top: targetRect.top - 6,
            left: targetRect.left - 6,
          };

          let tooltipTop = 0;
          let arrowStyle: React.CSSProperties = {};

          // Position tooltip below if there is space, otherwise above
          if (targetRect.bottom + tooltipRect.height + margin < innerHeight) {
              tooltipTop = targetRect.bottom + margin;
              arrowStyle = {
                  top: '-7px',
                  left: '50%',
                  transform: 'translateX(-50%) rotate(45deg)',
                  borderBottomColor: 'transparent',
                  borderRightColor: 'transparent',
              };
          } else {
              tooltipTop = targetRect.top - tooltipRect.height - margin;
              arrowStyle = {
                  bottom: '-7px',
                  left: '50%',
                  transform: 'translateX(-50%) rotate(45deg)',
                  borderTopColor: 'transparent',
                  borderLeftColor: 'transparent',
              };
          }

          let tooltipLeft = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
          // Clamp to viewport horizontally
          tooltipLeft = Math.max(margin, Math.min(tooltipLeft, innerWidth - tooltipRect.width - margin));
          
          // Final check to prevent vertical overflow on small screens or with large elements
          if (tooltipTop < margin) {
            tooltipTop = margin;
          } else if (tooltipTop + tooltipRect.height + margin > innerHeight) {
            tooltipTop = innerHeight - tooltipRect.height - margin;
          }

          setStyles({
            highlight: highlightStyle,
            tooltip: { top: `${tooltipTop}px`, left: `${tooltipLeft}px` },
            arrow: arrowStyle,
          });

        } else { // No target, center the tooltip (modal style)
          setStyles({
            highlight: { display: 'none' },
            tooltip: {
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            },
            arrow: { display: 'none' },
          });
        }
        
        setTimeout(() => setIsVisible(true), 50);
      }, positionDelay);
    };

    const timer = setTimeout(calculatePosition, step.action ? 300 : 50);
    return () => clearTimeout(timer);

  }, [currentStep, isOpen]);
  
  if (!isOpen) return null;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="fixed inset-0 z-50 no-print">
      <div className="tour-highlight" style={styles.highlight} />
      
      <div
        ref={tooltipRef}
        className={`tour-tooltip ${isVisible ? 'visible' : ''}`}
        style={styles.tooltip}
      >
        <button 
          onClick={onComplete} 
          className="absolute top-3 right-3 p-1 text-slate-500 hover:text-white transition-colors rounded-full focus:outline-none focus:ring-2 focus:ring-slate-400"
          aria-label="End tour"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
        <div className="tour-arrow" style={styles.arrow}></div>
        <div className="flex flex-col text-center items-center">
            {/* Only show the large icon for modal steps (no target) */}
            {!step.target && <div className="mb-4">{step.icon}</div>}
            <h2 className="text-xl font-bold text-slate-100 tracking-tight mb-3">{step.title}</h2>
            <p className="text-sm text-slate-300 mb-6 leading-relaxed">{step.content}</p>
            
            <div className="w-full mt-auto pt-4 border-t border-slate-700/50">
                <div className="flex justify-between items-center">
                    <div className="text-xs font-semibold bg-blue-600/30 border border-blue-500/50 text-blue-300 px-3 py-1 rounded-full">
                        Step {currentStep + 1} / {steps.length}
                    </div>
                    <div className="flex gap-3">
                        {currentStep > 0 && (
                            <button onClick={handlePrev} className="bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-3 rounded-lg transition-all duration-200 transform hover:scale-105 hover:brightness-110 flex items-center gap-2 text-sm">
                                <ArrowLeftIcon className="w-4 h-4" /> Prev
                            </button>
                        )}
                        {currentStep < steps.length - 1 ? (
                            <button onClick={handleNext} className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-2 px-3 rounded-lg transition-all duration-200 transform hover:scale-105 hover:brightness-110 flex items-center gap-2 text-sm">
                                Next <ArrowRightIcon className="w-4 h-4" />
                            </button>
                        ) : (
                            <button onClick={onComplete} className="bg-green-600 hover:bg-green-500 text-white font-bold py-2 px-3 rounded-lg transition-all duration-200 transform hover:scale-105 hover:brightness-110 text-sm">
                                Finish
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default AppTour;